import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import "./AdminHackingMatches.css";

export default function AdminHackingMatches() {
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);

  /* ===============================
     FETCH BETS
  =============================== */
  useEffect(() => {
    fetchBets();
  }, []);

  const fetchBets = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "userBets"));
      const list = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setBets(list);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  /* ===============================
     DATE CATEGORY
  =============================== */
  const getDateCategory = (bet) => {
    if (!bet.matchDate || !bet.matchTime) return "PAST";

    const d = new Date(`${bet.matchDate}T${bet.matchTime}`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const weekStart = new Date(today);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());

    if (d >= today && d < tomorrow) return "TODAY";
    if (d >= yesterday && d < today) return "YESTERDAY";
    if (d >= weekStart && d < today) return "THIS_WEEK";
    if (d >= tomorrow) return "TOMORROW";

    return "PAST";
  };

  /* ===============================
     🚨 1. DUPLICATE MATCH (ALL TIME)
  =============================== */
  const duplicateHistory = (() => {
    const map = {};

    bets.forEach((bet) => {
      const key = `${bet.username}|${bet.teamA}|${bet.teamB}|${bet.betInTsh || 0}|${bet.betInUsdt || 0}`;

      if (!map[key]) {
        map[key] = {
          username: bet.username,
          match: `${bet.teamA} vs ${bet.teamB}`,
          tsh: Number(bet.betInTsh || 0),
          usdt: Number(bet.betInUsdt || 0),
          period: getDateCategory(bet),
          bets: [],
        };
      }
      map[key].bets.push(bet);
    });

    return Object.values(map).filter(g => g.bets.length >= 2);
  })();

  /* ===============================
     🚨 2. DOUBLE CLICK DETECTION
     (bets within 5 seconds)
  =============================== */
  const doubleClickBets = (() => {
    const result = [];
    const grouped = {};

    bets.forEach((bet) => {
      if (!bet.betTime) return;

      const key = `${bet.username}|${bet.teamA}|${bet.teamB}|${bet.betInTsh || 0}|${bet.betInUsdt || 0}`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(bet);
    });

    Object.values(grouped).forEach((group) => {
      const sorted = [...group].sort(
        (a, b) => new Date(a.betTime) - new Date(b.betTime)
      );

      for (let i = 1; i < sorted.length; i++) {
        const prev = new Date(sorted[i - 1].betTime);
        const curr = new Date(sorted[i].betTime);
        const diffSeconds = (curr - prev) / 1000;

        if (diffSeconds <= 5) {
          result.push({
            username: sorted[i].username,
            match: `${sorted[i].teamA} vs ${sorted[i].teamB}`,
            tsh: Number(sorted[i].betInTsh || 0),
            usdt: Number(sorted[i].betInUsdt || 0),
            diffSeconds,
            bets: [sorted[i - 1], sorted[i]],
          });
        }
      }
    });

    return result;
  })();

  /* ===============================
     📊 MATCH SUMMARY
  =============================== */
  const matchSummary = (() => {
    const map = {};

    bets.forEach((bet) => {
      const key = `${bet.teamA} vs ${bet.teamB}`;
      if (!map[key]) {
        map[key] = {
          players: new Set(),
          tsh: 0,
          usdt: 0,
        };
      }
      map[key].players.add(bet.username);
      map[key].tsh += Number(bet.betInTsh || 0);
      map[key].usdt += Number(bet.betInUsdt || 0);
    });

    return Object.entries(map).map(([match, d]) => ({
      match,
      players: d.players.size,
      tsh: d.tsh,
      usdt: d.usdt,
    }));
  })();

  if (loading) return <p>Inapakia...</p>;

  return (
    <div className="admin-hacking-container">
      <h2>🛡️ Admin Hacking Matches (Double Click Detection)</h2>

      {/* ================= MATCH SUMMARY ================= */}
      <h3>📊 Match Summary</h3>
      <table className="summary-table">
        <thead>
          <tr>
            <th>Match</th>
            <th>Players</th>
            <th>Total TSH</th>
            <th>Total USDT</th>
          </tr>
        </thead>
        <tbody>
          {matchSummary.map((m) => (
            <tr key={m.match}>
              <td>{m.match}</td>
              <td>{m.players}</td>
              <td>{m.tsh.toLocaleString()}</td>
              <td>{m.usdt.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ================= DOUBLE CLICK ================= */}
      <h3>⚠️ Double Click Bets (≤ 5 seconds)</h3>
      <table className="bets-table highlighted-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Match</th>
            <th>TSH</th>
            <th>USDT</th>
            <th>Time Difference</th>
            <th>Bet Times</th>
          </tr>
        </thead>
        <tbody>
          {doubleClickBets.map((d, i) => (
            <tr key={i}>
              <td>{d.username}</td>
              <td>{d.match}</td>
              <td>{d.tsh.toLocaleString()}</td>
              <td>{d.usdt.toFixed(2)}</td>
              <td>{d.diffSeconds}s</td>
              <td>
                {d.bets.map((b, idx) => (
                  <div key={idx}>
                    {new Date(b.betTime).toLocaleString()}
                  </div>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ================= HISTORY DUPLICATES ================= */}
      <h3>🚨 Duplicate Match History (All Time)</h3>
      <table className="bets-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Match</th>
            <th>TSH</th>
            <th>USDT</th>
            <th>Times Played</th>
            <th>Period</th>
          </tr>
        </thead>
        <tbody>
          {duplicateHistory.map((d, i) => (
            <tr key={i}>
              <td>{d.username}</td>
              <td>{d.match}</td>
              <td>{d.tsh.toLocaleString()}</td>
              <td>{d.usdt.toFixed(2)}</td>
              <td>{d.bets.length}</td>
              <td>{d.period}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}